import React from "react";
import { CalendarDays, Rows3 } from "lucide-react";
import { DayStripCalendar } from "./DayStripCalendar";
import { SlotMonthCalendar } from "./SlotMonthCalendar";

/**
 * The workspace's one date picker, in either of its two shapes.
 *
 * `DayStripCalendar` and `SlotMonthCalendar` already take an identical prop
 * contract — they were built on the same `buildMonthGrid` and kept in step on
 * purpose — so offering both is a component swap rather than a second
 * implementation. The strip is the default because picking a day near today is
 * the common case and it costs one row; the grid is there for the other one,
 * planning a month of availability, where you need to see the whole month at
 * once.
 *
 * Both are opened all the way up (`allowPast allowClosed`): it is the bookings
 * calendar, and a past day or a Sunday that can't be *selected* is a booking
 * nobody can reach. (Shifts pick a week, with their own picker.)
 *
 * @param {Object} props
 * @param {number} props.year
 * @param {number} props.month - 0-indexed.
 * @param {(year: number, month: number) => void} props.onMonthChange
 * @param {Date|null} props.selectedDate
 * @param {(date: Date) => void} props.onSelectDate
 * @param {Record<string, Array<Object>>} props.markedByDate - What the dots
 *   mean follows the active sub-tab; see `BookingsWorkspace`.
 * @param {string} props.markLabel
 * @param {boolean} [props.busy]
 * @param {boolean} props.monthView - Grid when true, strip when false.
 * @param {(next: boolean) => void} props.onMonthViewChange
 */
export const WorkspaceCalendar = ({
  year,
  month,
  onMonthChange,
  selectedDate,
  onSelectDate,
  markedByDate,
  markLabel,
  busy = false,
  monthView,
  onMonthViewChange,
}) => {
  const shared = {
    year,
    month,
    onMonthChange,
    selectedDate,
    onSelectDate,
    markedByDate,
    markLabel,
    busy,
    allowPast: true,
    allowClosed: true,
  };

  return (
    <div className="flex items-start gap-3">
      {/*
        The grid is capped the way it always was — at full width it renders as
        a very wide, very sparse table — while the strip takes whatever is
        left, which is what makes it a strip.
      */}
      <div className={monthView ? "min-w-0 max-w-xl flex-1" : "min-w-0 flex-1"}>
        {monthView ? (
          <SlotMonthCalendar {...shared} />
        ) : (
          <DayStripCalendar {...shared} />
        )}
      </div>

      <button
        type="button"
        onClick={() => onMonthViewChange(!monthView)}
        aria-pressed={monthView}
        title={monthView ? "Switch to day view" : "Switch to month view"}
        className="inline-flex min-h-[40px] flex-shrink-0 items-center gap-2 rounded-lg border border-bc-hairline px-3 text-xs font-semibold text-bc-text-2 transition-colors hover:bg-light-orange/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1"
      >
        {monthView ? (
          <Rows3 className="h-4 w-4" aria-hidden="true" />
        ) : (
          <CalendarDays className="h-4 w-4" aria-hidden="true" />
        )}
        {/* The label names the destination, not the current state — a toggle
            that reads as a description of where you already are is a toggle
            people press twice. */}
        <span className="hidden sm:inline">
          {monthView ? "Day view" : "Month view"}
        </span>
      </button>
    </div>
  );
};
